import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { SignupFormValues, signupSchema } from "@/lib/schemas";
import { signUp } from "@/lib/supabase/auth";
import { toast } from "sonner";
import { AuthError } from "@/lib/supabase/errors";
import { debug } from "@/lib/utils";
import TradesPurpleLayout from "../components/Layout";
import { Helmet } from "react-helmet-async";

export default function TradesPurpleSignupPage() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: SignupFormValues) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password);

      toast.success("Signup successful! You'll now need to set up your TradesPurple profile.");

      // Navigate to onboarding immediately - no need for delay
      // The auth state will be available on the onboarding page
      navigate("/auth/onboarding", { replace: true });
    } catch (error) {
      if (error instanceof AuthError) {
        toast.error(error.message);
      } else {
        toast.error("Failed to sign up");
      }
      debug.error("Signup error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>TradesPurple | Create Account</title>
        <meta name="description" content="Create a TradesPurple account to save and share your trusted tradespeople." />
      </Helmet>

      <div className="flex items-center justify-center">
        <Card className="w-full border-2 border-purple-900 rounded-xl shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center" style={{ color: "#5D4A9C" }}>Create your account</CardTitle>
            <CardDescription className="text-center text-purple-700">
              Join TradesPurple to save and share trusted tradespeople
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={{ color: "#5D4A9C" }}>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          className="border-2 border-purple-300 focus:border-purple-500"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={{ color: "#5D4A9C" }}>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className="border-2 border-purple-300 focus:border-purple-500"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={{ color: "#5D4A9C" }}>Confirm Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className="border-2 border-purple-300 focus:border-purple-500"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full bg-purple-700 hover:bg-purple-800"
                  disabled={isLoading}
                >
                  {isLoading ? "Creating account..." : "Sign up"}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <div className="text-sm text-center text-purple-700">
              Already have an account?{" "}
              <Link to="/auth/login" style={{ color: "#5D4A9C" }} className="font-semibold hover:underline">
                Log in
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </TradesPurpleLayout>
  );
}
